import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader } from 'shared/ui/Loader/Loader';

import { HelpRequestSectionSubTitle } from './HelpRequestComponents';

import { HELP_OPTIONS, MAX_DETAILS_LENGTH } from '../model/consts';

interface HelpRequestFormProps {
    isSubmitting: boolean;
    onSubmit: (issueType: string, body: string) => void;
}

export const HelpRequestForm = memo(
    ({ isSubmitting, onSubmit }: HelpRequestFormProps) => {
        const { t } = useTranslation();

        const [selectedOption, setSelectedOption] = useState<string>('');
        const [details, setDetails] = useState<string>('');

        const handleSubmit = () => {
            if (selectedOption) {
                onSubmit(selectedOption, details);
            }
        };

        return (
            <FormControl component="fieldset" fullWidth>
                <HelpRequestSectionSubTitle
                    title={t('What do you need help with?')}
                />

                <RadioGroup
                    data-testid="help-request-radio-group"
                    value={selectedOption}
                    onChange={(e) => setSelectedOption(e.target.value)}
                >
                    {HELP_OPTIONS.map((option) => (
                        <FormControlLabel
                            key={option}
                            value={option}
                            disabled={isSubmitting}
                            control={
                                <Radio
                                    sx={{
                                        color: 'text.secondary',
                                        '&.Mui-checked': {
                                            color: isSubmitting
                                                ? ''
                                                : 'primary.main',
                                        },
                                        height: '24px',
                                        width: '24px',
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ ml: 1 }} variant="body2">
                                    {t(option)}
                                </Typography>
                            }
                            sx={{
                                '&.MuiFormControlLabel-root': {
                                    marginLeft: 0,
                                    marginBottom: '12px',
                                },
                                '&:last-child': {
                                    mb: 0,
                                },
                            }}
                        />
                    ))}
                </RadioGroup>

                <Box mt="24px">
                    <HelpRequestSectionSubTitle
                        title={t(
                            'Share any important details we should know (optional)',
                        )}
                    />

                    <TextField
                        data-testid="help-request-textarea"
                        multiline
                        rows={6}
                        fullWidth
                        label="Details"
                        placeholder={t(
                            'Example: I ordered the 20x20x1 filter, but received the 16x20x1 size.',
                        )}
                        value={details}
                        onChange={(e) => setDetails(e.target.value)}
                        helperText={
                            <Box component="span">
                                <Box component="span" color="grey.600">
                                    {details.length}
                                </Box>
                                <Box component="span" color="common.black">
                                    {' / '}
                                    {MAX_DETAILS_LENGTH}
                                </Box>
                            </Box>
                        }
                        disabled={isSubmitting}
                        inputProps={{ maxLength: MAX_DETAILS_LENGTH }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        sx={{
                            '& .MuiFormHelperText-root': {
                                textAlign: 'right',
                                fontSize: '1.2rem',
                            },
                        }}
                    />
                </Box>

                <Button
                    data-testid="btn-help-request-submit"
                    variant="contained"
                    startIcon={isSubmitting && <Loader color="inherit" />}
                    onClick={handleSubmit}
                    disabled={!selectedOption || isSubmitting}
                    sx={{
                        px: '16px',
                        width: '200px',
                        '&.Mui-disabled': {
                            cursor: 'not-allowed',
                        },
                    }}
                >
                    {t('Send help request')}
                </Button>
            </FormControl>
        );
    },
);
HelpRequestForm.displayName = 'HelpRequestForm';
